import { useMsal } from '@azure/msal-react'
import { faBell, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Paper,
  Skeleton,
  Stack,
  Text,
  Title,
} from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNotifications } from '../../contexts/NotificationContext'
import { useSessionInfo } from '../../contexts/SessionContext'
import {
  fetchNotifications,
  markAllNotificationsAsRead,
  markNotificationAsRead,
} from '../../data/notification.service'
import { Notification } from '../../models/Notification'
import { COLORS } from '../../utils/constants'
import { convertServerDateToLocal } from '../../utils/dateUtils'

interface NotificationsProps {
  onClose?: () => void
}

const Notifications = ({ onClose }: NotificationsProps) => {
  const { instance } = useMsal()
  const { session } = useSessionInfo()
  const { refreshUnreadCount } = useNotifications()
  const navigate = useNavigate()
  const [notificationsList, setNotificationsList] = useState<Notification[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isHostMode, setIsHostMode] = useLocalStorage({
    key: 'isHostMode',
    defaultValue: false,
  })

  useEffect(() => {
    const fetchNotificationsData = async () => {
      if (!session) {
        console.log('Session not available, skipping notifications fetch')
        return
      }

      setIsLoading(true)
      try {
        const data = await fetchNotifications(instance, session.organizationId)
        setNotificationsList(data)
      } catch (error) {
        console.error('Error fetching notifications:', error)
        notifications.show({
          title: 'Error',
          message: 'Failed to load notifications',
          color: 'red',
        })
      } finally {
        setIsLoading(false)
      }
    }

    fetchNotificationsData()
  }, [instance, session])

  const handleMarkAsRead = async (id: number) => {
    try {
      await markNotificationAsRead(id, instance)
      setNotificationsList((prev) =>
        prev.map((notification) =>
          notification.id === id
            ? { ...notification, isRead: true }
            : notification,
        ),
      )
      // Refresh the unread count in the context
      refreshUnreadCount()
    } catch (error) {
      console.error('Error marking notification as read:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to mark notification as read',
        color: 'red',
      })
    }
  }

  const handleMarkAllAsRead = async () => {
    try {
      await markAllNotificationsAsRead(instance, session!.organizationId)
      setNotificationsList((prev) =>
        prev.map((notification) => ({ ...notification, isRead: true })),
      )
      // Refresh the unread count in the context
      refreshUnreadCount()
      notifications.show({
        title: 'Success',
        message: 'All notifications marked as read',
        color: 'green',
      })
    } catch (error) {
      console.error('Error marking all notifications as read:', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to mark all notifications as read',
        color: 'red',
      })
    }
  }

  const handleViewDetails = async (
    url: string,
    notificationId: number,
    notificationType: string,
  ) => {
    // Mark the notification as read first
    try {
      await markNotificationAsRead(notificationId, instance)
      setNotificationsList((prev) =>
        prev.map((notification) =>
          notification.id === notificationId
            ? { ...notification, isRead: true }
            : notification,
        ),
      )
      // Refresh the unread count in the context
      refreshUnreadCount()
    } catch (error) {
      console.error('Error marking notification as read:', error)
    }

    // Switch to appropriate mode based on notification type
    if (notificationType === 'host' && !isHostMode) {
      setIsHostMode(true)
    } else if (notificationType === 'advertiser' && isHostMode) {
      setIsHostMode(false)
    }

    // Then navigate to the details
    if (url.startsWith('/dashboard/')) {
      // Internal route - use React Router navigation
      navigate(url)
    } else {
      // External URL - open in new tab
      window.open(url, '_blank')
    }
  }

  const unreadCount = notificationsList.filter((n) => !n.isRead).length

  return (
    <div
      style={{
        backgroundColor: 'white',
        width: '100%',
        height: '100%',
        position: 'relative',
        overflowY: 'auto',
      }}
    >
      <Stack p={24} gap={24}>
        {/* Header */}
        <Group justify='space-between' align='center'>
          <Group>
            <FontAwesomeIcon
              icon={faBell}
              size='lg'
              color={COLORS.LightGreen}
            />
            <Title order={2}>Notifications</Title>
            {unreadCount > 0 && (
              <Badge color={COLORS.HiddenGemzRed} size='lg'>
                {unreadCount}
              </Badge>
            )}
          </Group>
          <Group>
            {unreadCount > 0 && (
              <Button
                variant='subtle'
                color={COLORS.HiddenGemzRed}
                onClick={handleMarkAllAsRead}
              >
                Mark all as read
              </Button>
            )}
            {onClose && (
              <ActionIcon
                variant='subtle'
                size='lg'
                onClick={onClose}
                color={COLORS.LightGreen}
              >
                <FontAwesomeIcon icon={faTimes} />
              </ActionIcon>
            )}
          </Group>
        </Group>

        {/* Notifications List */}
        {isLoading ? (
          <Stack gap={16}>
            {[...Array(5)].map((_, index) => (
              <Paper key={index} p={16} withBorder>
                <Stack gap={8}>
                  <Group justify='space-between' align='flex-start'>
                    <Stack gap={4} style={{ flex: 1 }}>
                      <Skeleton height={24} width='60%' />
                      <Skeleton height={16} width='40%' />
                    </Stack>
                  </Group>
                  <Skeleton height={16} width='100%' />
                  <Skeleton height={16} width='80%' />
                  <Group justify='flex-end'>
                    <Skeleton height={24} width={80} />
                  </Group>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : notificationsList.length > 0 ? (
          <Stack gap={16}>
            {notificationsList.map((notification) => (
              <Paper
                key={notification.id}
                p={16}
                withBorder
                style={{
                  borderLeft: notification.isRead
                    ? undefined
                    : `4px solid ${COLORS.HiddenGemzRed}`,
                  backgroundColor: notification.isRead ? undefined : '#f8f9fa',
                }}
              >
                <Stack gap={8}>
                  <Group justify='space-between' align='flex-start'>
                    <Stack gap={4} style={{ flex: 1 }}>
                      <Text
                        fw={600}
                        size='lg'
                        c={
                          notification.isRead ? undefined : COLORS.HiddenGemzRed
                        }
                      >
                        {notification.title}
                      </Text>
                    </Stack>
                    <Text size='sm' c='dimmed'>
                      {convertServerDateToLocal(
                        notification.createdDate,
                      )?.toLocaleDateString()}{' '}
                      at{' '}
                      {convertServerDateToLocal(
                        notification.createdDate,
                      )?.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </Text>
                  </Group>
                  <Text size='sm' style={{ lineHeight: 1.5 }}>
                    {notification.message}
                  </Text>
                  <Group justify='space-between'>
                    {notification.url ? (
                      <Button
                        size='xs'
                        color={COLORS.LightGreen}
                        onClick={() =>
                          handleViewDetails(
                            notification.url!,
                            notification.id,
                            notification.type,
                          )
                        }
                      >
                        View Details
                      </Button>
                    ) : (
                      <div></div>
                    )}
                    {!notification.isRead && (
                      <Button
                        variant='subtle'
                        size='xs'
                        color={COLORS.HiddenGemzRed}
                        onClick={() => handleMarkAsRead(notification.id)}
                      >
                        Mark as read
                      </Button>
                    )}
                  </Group>
                </Stack>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Stack align='center' gap='md' py='xl'>
            <FontAwesomeIcon
              icon={faBell}
              size='2x'
              color={COLORS.BasicTextGray}
            />
            <Text size='lg' c='dimmed' ta='center'>
              No notifications to show
            </Text>
            <Text size='sm' c='dimmed' ta='center'>
              You'll see notifications here when there are updates about your
              advertisements
            </Text>
          </Stack>
        )}
      </Stack>
    </div>
  )
}

export default Notifications
